// src/components/AncientModernBridge.jsx
import React, { useEffect, useRef, useState } from 'react';

const AncientModernBridge = ({ className = "" }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        // Set canvas size
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        const width = rect.width;
        const height = rect.height;

        // Particles flowing from ancient to modern
        const particles = [];
        const maxParticles = 8;

        class Particle {
            constructor() {
                this.reset();
                // Start at random position along the path for initial spread
                this.progress = Math.random();
            }

            reset() {
                this.progress = 0;
                this.opacity = 0;
                this.size = Math.random() * 2 + 1;
                this.speed = 0.003 + Math.random() * 0.002;
                this.wobble = Math.random() * 0.5;
                this.wobbleSpeed = 0.02 + Math.random() * 0.01;
            }

            update(time) {
                this.progress += this.speed;

                // Fade in, stay visible, then fade out
                if (this.progress < 0.1) {
                    this.opacity = this.progress * 10;
                } else if (this.progress > 0.9) {
                    this.opacity = (1 - this.progress) * 10;
                } else {
                    this.opacity = 0.6 + Math.sin(time * 0.01) * 0.2;
                }

                if (this.progress >= 1) {
                    this.reset();
                }
            }

            getPosition(width, height) {
                // Bezier curve from left to right with slight arc
                const startX = width * 0.15;
                const endX = width * 0.85;
                const startY = height * 0.5;
                const endY = height * 0.5;
                const controlY = height * 0.3; // Upward arc

                const t = this.progress;
                const invT = 1 - t;

                // Quadratic bezier curve
                const x = invT * invT * startX + 2 * invT * t * (startX + endX) / 2 + t * t * endX;
                const y = invT * invT * startY + 2 * invT * t * controlY + t * t * endY;

                // Add subtle wobble
                const wobbleOffset = Math.sin(this.progress * Math.PI * 4 + this.wobble) * 8;

                return { x, y: y + wobbleOffset };
            }

            draw(ctx, width, height) {
                if (this.opacity <= 0) return;

                const pos = this.getPosition(width, height);

                ctx.save();
                ctx.globalAlpha = this.opacity;

                // Create glowing effect
                const gradient = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, this.size * 3);
                gradient.addColorStop(0, '#60a5fa');
                gradient.addColorStop(0.5, '#3b82f6');
                gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');

                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, this.size * 3, 0, Math.PI * 2);
                ctx.fill();

                // Inner bright core
                ctx.fillStyle = '#60a5fa';
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, this.size, 0, Math.PI * 2);
                ctx.fill();

                ctx.restore();
            }
        }

        // Initialize particles
        for (let i = 0; i < maxParticles; i++) {
            particles.push(new Particle());
        }

        // Draw connection line
        const drawConnectionLine = (ctx, width, height, time) => {
            const startX = width * 0.15;
            const endX = width * 0.85;
            const y = height * 0.5;

            ctx.save();

            // Main connection line with gradient
            const gradient = ctx.createLinearGradient(startX, y, endX, y);
            gradient.addColorStop(0, 'rgba(96, 165, 250, 0.2)');
            gradient.addColorStop(0.5, 'rgba(96, 165, 250, 0.6)');
            gradient.addColorStop(1, 'rgba(96, 165, 250, 0.2)');

            ctx.strokeStyle = gradient;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';

            // Animate the line opacity
            ctx.globalAlpha = 0.4 + Math.sin(time * 0.002) * 0.2;

            ctx.beginPath();
            ctx.moveTo(startX, y);

            // Draw curved line
            const controlX = width * 0.5;
            const controlY = height * 0.3;
            ctx.quadraticCurveTo(controlX, controlY, endX, y);
            ctx.stroke();

            // Draw connection points
            ctx.fillStyle = '#60a5fa';
            ctx.globalAlpha = 0.8 + Math.sin(time * 0.003) * 0.2;

            // Ancient side (left)
            ctx.beginPath();
            ctx.arc(startX, y, 4, 0, Math.PI * 2);
            ctx.fill();

            // Modern side (right)
            ctx.beginPath();
            ctx.arc(endX, y, 4, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        };

        // Animation loop
        let time = 0;
        const animate = () => {
            if (!isInView) {
                animationRef.current = requestAnimationFrame(animate);
                return;
            }

            ctx.clearRect(0, 0, width, height);

            // Draw connection line
            drawConnectionLine(ctx, width, height, time);

            // Update and draw particles
            particles.forEach(particle => {
                particle.update(time);
                particle.draw(ctx, width, height);
            });

            time += 16; // Approximate 60fps
            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isInView]);

    // Intersection Observer to start animation when in view
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsInView(entry.isIntersecting);
            },
            { threshold: 0.3 }
        );

        observer.observe(canvas);

        return () => observer.disconnect();
    }, []);

    return (
        <div className={`flex items-center justify-center my-8 ${className}`}>
            <canvas
                ref={canvasRef}
                className="w-full max-w-lg mx-auto"
                style={{
                    width: '100%',
                    maxWidth: '800px',
                    height: '40px',
                    opacity: isInView ? 1 : 0,
                    transition: 'opacity 1s ease-in-out'
                }}
                aria-label="Animated connection between ancient and modern principles"
            />
        </div>
    );
};

export default AncientModernBridge;